/**
 * The workspaces of the environment worked in, beside the environment bar: one tab per
 * workspace (boards sharing nothing, like Coding's app and a game beside it), the one this page
 * works in pressed, and "+" to make one. A double click renames a tab; its right-click menu
 * renames or removes it (with all it holds, one undo). An environment with no workspace shows
 * only "+": the first one made then keeps what the environment already holds, under its name.
 */
import { createSignal, For, Show, type JSX } from "solid-js";
import { useApp } from "./context";

/** What is being typed: a new workspace's name, or a new name for workspace `id`. */
type Naming = { kind: "new" } | { kind: "rename"; id: string };

/** The workspace tabs of the current environment. */
export function WorkspaceBar(): JSX.Element {
  const { environments, actions, menu, access } = useApp();
  const [naming, setNaming] = createSignal<Naming | null>(null);
  const mayEdit = () => access.can("editor");

  /** Makes workspace `name` (and, in an environment with none yet, the one keeping what it holds), then works in it. */
  async function create(name: string) {
    const environment = environments.current();
    if (!environment || !name.trim()) return;
    const names = environments.workspaces().length ? [name] : [environment.label, name];
    const changes = await actions.addWorkspaces(environment.id, names);
    const set = changes?.find((c) => c.type === "workspaces_set");
    const made = set?.type === "workspaces_set" ? set.after[set.after.length - 1] : undefined;
    if (made) environments.chooseWorkspace(made.id);
  }

  /** Ends the typing: `name` is kept (Enter, leaving the field) or dropped (`null`: Escape). */
  function finish(name: string | null) {
    const was = naming();
    setNaming(null);
    if (!was || name === null || !name.trim()) return;
    if (was.kind === "new") void create(name.trim());
    else void actions.renameWorkspace(was.id, name.trim());
  }

  /** The field where a name is typed. */
  const field = (id: string, value: string) => (
    <input
      id={id}
      class="ws-name"
      value={value}
      placeholder="Workspace name"
      ref={(el) => requestAnimationFrame(() => el.select())}
      onKeyDown={(e) => {
        if (e.key === "Enter") finish(e.currentTarget.value);
        if (e.key === "Escape") finish(null);
      }}
      onBlur={(e) => naming() && finish(e.currentTarget.value)}
    />
  );

  return (
    <Show when={environments.current()}>
      <div class="wsbar" id="workspaces" role="toolbar" aria-label="Workspaces">
        <For each={environments.workspaces()}>
          {(w) => (
            <Show
              when={naming()?.kind === "rename" && (naming() as { id: string }).id === w.id}
              fallback={
                <button
                  type="button"
                  class="ws-button"
                  data-workspace={w.id}
                  title={`Workspace ${w.name}: a board of its own in ${environments.current()?.label ?? "this environment"}`}
                  aria-pressed={environments.workspace()?.id === w.id}
                  onClick={() => environments.chooseWorkspace(w.id)}
                  onDblClick={() => mayEdit() && setNaming({ kind: "rename", id: w.id })}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    menu.open(e, [
                      { label: "Work here", action: () => environments.chooseWorkspace(w.id), disabled: environments.workspace()?.id === w.id },
                      { label: "Rename…", action: () => setNaming({ kind: "rename", id: w.id }), disabled: !mayEdit() },
                      { separator: true },
                      { label: "Remove the workspace", danger: true, confirm: `Remove ${w.name} and all it holds?`, action: () => void actions.removeWorkspace(w.id), disabled: !mayEdit() },
                    ]);
                  }}
                >
                  {w.name}
                </button>
              }
            >
              {field("workspace-rename", w.name)}
            </Show>
          )}
        </For>
        <Show
          when={naming()?.kind === "new"}
          fallback={
            <button
              type="button"
              class="ws-button ws-add"
              id="workspace-add"
              title={`New workspace in ${environments.current()?.label ?? "this environment"}: a board of its own`}
              disabled={!mayEdit()}
              onClick={() => setNaming({ kind: "new" })}
            >
              +
            </button>
          }
        >
          {field("workspace-name", "")}
        </Show>
      </div>
    </Show>
  );
}
